/** <mobile-menu>: full-screen mobile nav drawer — opened by data-mm-open, closed by data-mm-close/Escape, data-mm-to opens a sub-view and data-mm-back closes it; first-level accordions handled by <accordion-component>. */
(function () {
  if (customElements.get("mobile-menu")) return;

  class MobileMenu extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector(".mobile-menu__panel");
      // Fermé au départ : hors tabulation / lecteurs d'écran.
      if (this.panel) this.panel.inert = true;

      // Expose la hauteur du header en CSS var (drawer sous le header), recalculée au resize.
      this._setHeaderHeight();
      this._onResize = () => this._setHeaderHeight();
      window.addEventListener("resize", this._onResize);
      // The zoom lock rescales the header, so its painted height must be republished — on its own event, since both listeners answer the same resize and the order is not ours to choose.
      window.addEventListener("zoom-lock:change", this._onResize);

      // Burger : délégation sur document (robuste aux re-renders) ; même bouton ouvre/ferme.
      this._onDocClick = (e) => {
        const opener = e.target.closest("[data-mm-open]");
        if (opener && opener.getAttribute("aria-controls") === this.id) {
          this._trigger = opener;
          if (!this.hasAttribute("data-open")) {
            this.open();
          } else if (this.querySelector(".mobile-menu__panel--sub.is-open")) {
            // Dans un second drawer : la croix ne referme que ce drawer.
            this.closeViews();
          } else {
            this.close();
          }
        }
      };
      document.addEventListener("click", this._onDocClick);

      // Actions internes (fermer / retour / ouvrir une sous-vue).
      this.addEventListener("click", (e) => {
        if (e.target.closest("[data-mm-close]")) {
          this.close();
          return;
        }
        if (e.target.closest("[data-mm-back]")) {
          this.closeViews();
          return;
        }
        const to = e.target.closest("[data-mm-to]");
        if (to) this.openView(to.getAttribute("data-mm-to"));
      });

      this._onKeydown = (e) => {
        if (e.key === "Tab") {
          this._trapTab(e);
          return;
        }
        if (e.key !== "Escape") return;
        // Escape referme d'abord le second drawer ouvert, sinon tout le drawer.
        if (this.querySelector(".mobile-menu__panel--sub.is-open")) this.closeViews();
        else this.close();
      };
    }

    disconnectedCallback() {
      document.removeEventListener("click", this._onDocClick);
      document.removeEventListener("keydown", this._onKeydown);
      window.removeEventListener("resize", this._onResize);
      window.removeEventListener("zoom-lock:change", this._onResize);
    }

    /** Focusables réellement atteignables du panneau ; `visibility` étant héritée, tester l'élément seul écarte aussi une vue repliée ou une sous-vue fermée. */
    _focusables() {
      if (!this.panel) return [];
      const selector =
        'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
      return [...this.panel.querySelectorAll(selector)].filter((el) => {
        const style = getComputedStyle(el);
        return style.visibility !== "hidden" && style.display !== "none";
      });
    }

    /** Le panneau annonce aria-modal, donc Tab doit tourner dedans au lieu de parcourir la page laissée derrière — 162 éléments y restaient atteignables. */
    _trapTab(event) {
      const items = this._focusables();
      if (!items.length) return;

      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      const outside = !this.panel.contains(active);

      if (event.shiftKey ? active === first || outside : active === last || outside) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      }
    }

    /** Publie la hauteur du header dans --header-height (positionnement du drawer). */
    _setHeaderHeight() {
      const header = document.querySelector(".header");
      if (header) {
        // getBoundingClientRect, not offsetHeight: under the zoom lock the header is scaled by CSS `zoom`, which offsetHeight ignores. Everything reading this variable positions itself in page pixels under the header, so it needs the height actually painted.
        const height = Math.round(header.getBoundingClientRect().height);
        document.documentElement.style.setProperty("--header-height", height + "px");
      }
    }

    open() {
      this.setAttribute("data-open", "");
      this._scrollY = window.scrollY;
      document.documentElement.style.overflow = "hidden";
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.top = `-${this._scrollY}px`;
      document.body.style.left = "0";
      document.body.style.right = "0";
      document.body.style.width = "100%";
      if (this.panel) this.panel.inert = false;
      this._trigger?.setAttribute("aria-expanded", "true");
      document.addEventListener("keydown", this._onKeydown);

      // Met Lenis en pause : sinon overflow:hidden ne suffit pas (scroll piloté par GSAP/Lenis).
      this.dispatchEvent(new CustomEvent("drawer:open", { bubbles: true }));

      requestAnimationFrame(() => {
        this._focusables()[0]?.focus();
      });
    }

    close() {
      this.removeAttribute("data-open");
      document.documentElement.style.overflow = "";
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.left = "";
      document.body.style.right = "";
      document.body.style.width = "";
      window.scrollTo(0, this._scrollY || 0);
      if (this.panel) this.panel.inert = true;
      this.closeViews();
      this._trigger?.setAttribute("aria-expanded", "false");
      document.removeEventListener("keydown", this._onKeydown);
      this._trigger?.focus();

      // Relance Lenis (smooth scroll).
      this.dispatchEvent(new CustomEvent("drawer:close", { bubbles: true }));
    }

    /** Ouvre le second drawer `id` (ex. "sub-2") par-dessus le niveau 1. */
    openView(id) {
      this.closeViews();
      const view = this.querySelector('[data-mm-view="' + id + '"]');
      if (!view) return;
      view.inert = false;
      view.classList.add("is-open");
      requestAnimationFrame(() => view.querySelector("[data-mm-back]")?.focus());
    }

    /** Referme tout second drawer ouvert (retour au drawer de niveau 1). */
    closeViews() {
      this.querySelectorAll(".mobile-menu__panel--sub.is-open").forEach((v) => {
        v.classList.remove("is-open");
        v.inert = true;
      });
    }
  }

  customElements.define("mobile-menu", MobileMenu);
})();
